import { inject, Injectable } from '@angular/core';
import { InboxApplicationKey } from '@app/core/constants';
import { InboundFaxesQuery, InboxDto } from '@app/core/model';
import { LookupService } from '@app/core/service';
import { ColumnType, FilterTemplate } from '@app/shared/constants';
import { InboundFaxesTableModel, InboundFaxesTableModelKeys, TableColumnConfig } from '@app/shared/models';
import { FilterMetadata, SelectItem } from 'primeng/api';
import { TableLazyLoadEvent } from 'primeng/table';
import { InboundFaxesTableQueryService } from './inbound-faxes-table-query.service';

@Injectable({
  providedIn: 'root',
})
// Any attempts to have a method that updates the config should have a return type to ensure change detection captures the updates
export class InboundFaxesTableConfigService {
  public assignedUserClicked: (target: InboundFaxesTableModel) => void;
  public assignedMemberClicked: (target: InboundFaxesTableModel) => void;
  public faxStatusClicked: (target: InboundFaxesTableModel) => void;
  public requestTypeClicked: (target: InboundFaxesTableModel) => void;
  public previewClicked: (target: InboundFaxesTableModel) => void;
  public archiveFaxClicked: (target: InboundFaxesTableModel) => void;
  public restoreArchiveFaxClicked: (target: InboundFaxesTableModel) => void;

  private inboundFaxesTableQueryService = inject(InboundFaxesTableQueryService);
  private lookupService = inject(LookupService);

  public initTableConfig(isArchivedPage: boolean, selectedInbox: InboxDto = null): TableColumnConfig[] {
    const faxFolderName: TableColumnConfig = {
      header: 'Fax Folder',
      field: InboundFaxesTableModelKeys.faxFolderName,
      styles: { minWidth: '110px' },
      isVisible: true,
      isSortable: true,
      type: ColumnType.String,
      filterConfig: {
        template: FilterTemplate.MultiSelectFilterTemplate,
        options: [],
        values: [],
      },
    };

    const assignedUserName: TableColumnConfig = {
      header: 'Assigned User',
      field: InboundFaxesTableModelKeys.assignedUserName,
      styles: { minWidth: '110px' },
      isVisible: true,
      isSortable: false,
      type: isArchivedPage ? ColumnType.String : ColumnType.Button,
      buttonConfig: {
        buttonIcon: 'pi-times',
        emptyLabel: 'Assign User',
        emptyIcon: 'pi-user-plus',
        onClicked: (target: InboundFaxesTableModel) => this.assignedUserClicked(target),
        getToolTip: (target: InboundFaxesTableModel) => target.assignedUserName,
      },
      filterConfig: {
        template: FilterTemplate.MultiSelectFilterTemplate,
        options: [],
        values: [],
      },
    };

    const memberSubscriberNumber: TableColumnConfig = {
      header: 'Assigned Member',
      field: InboundFaxesTableModelKeys.memberSubscriberNumber,
      styles: { minWidth: '110px' },
      isSortable: true,
      isVisible: true,
      type: isArchivedPage ? ColumnType.String : ColumnType.Button,
      buttonConfig: {
        buttonIcon: 'pi-times',
        emptyLabel: 'Assign Member',
        emptyIcon: 'pi-user-plus',
        onClicked: (target: InboundFaxesTableModel) => this.assignedMemberClicked(target),
        getToolTip: (target: InboundFaxesTableModel) => {
          if (!!target.memberFullName)
            return `Name: ${target.memberFullName} \nDOB: ${target.memberDOB} \nHealth/Benefit Plan: \n ${target.memberBenefitPlan}`;
          return target.memberFullName;
        },
      },
      filterConfig: {
        template: FilterTemplate.SearchFilterTemplate,
        value: '',
      },
    };

    const faxReceivedDateTime: TableColumnConfig = {
      header: 'Date/Time Fax Received',
      field: InboundFaxesTableModelKeys.faxReceivedDateTime,
      styles: { minWidth: '110px' },
      isSortable: true,
      type: ColumnType.UtcToLocal,
      isVisible: true,
    };

    const statusName: TableColumnConfig = {
      header: 'Fax Status',
      field: InboundFaxesTableModelKeys.statusName,
      styles: { minWidth: '110px' },
      isVisible: true,
      isSortable: false,
      type: isArchivedPage ? ColumnType.String : ColumnType.Button,
      buttonConfig: {
        buttonIcon: 'pi-times',
        emptyLabel: 'Add Fax Status',
        emptyIcon: 'pi-plus',
        onClicked: (target: InboundFaxesTableModel) => this.faxStatusClicked(target),
        getToolTip: (target: InboundFaxesTableModel) => target.statusName,
      },
      filterConfig: {
        template: FilterTemplate.MultiSelectFilterTemplate,
        options: [],
        values: [],
      },
    };

    const requestTypeName: TableColumnConfig = {
      header: 'Request Type',
      field: InboundFaxesTableModelKeys.requestTypeName,
      styles: { minWidth: '110px' },
      isVisible: true,
      isSortable: false,
      type: isArchivedPage ? ColumnType.String : ColumnType.Button,
      buttonConfig: {
        buttonIcon: 'pi-times',
        emptyLabel: 'Add Request Type',
        emptyIcon: 'pi-plus',
        onClicked: (target: InboundFaxesTableModel) => {
          this.requestTypeClicked(target);
        },
        getToolTip: (target: InboundFaxesTableModel) => target.requestTypeName,
      },
      filterConfig: {
        template: FilterTemplate.MultiSelectFilterTemplate,
        options: [],
        values: [],
      },
    };

    const note: TableColumnConfig = {
      header: 'Fax Notes',
      field: InboundFaxesTableModelKeys.noteId,
      styles: { minWidth: '110px' },
      isSortable: false,
      type: ColumnType.NoteButton,
      isVisible: true,
    };

    const archivedReasonDescription: TableColumnConfig = {
      header: 'Archival Reason',
      field: InboundFaxesTableModelKeys.archivedReasonDescription,
      styles: { minWidth: '110px' },
      isSortable: true,
      type: ColumnType.String,
      isVisible: isArchivedPage,
    };

    const timeInFaxFolder: TableColumnConfig = {
      header: 'Time in Fax Folder',
      field: InboundFaxesTableModelKeys.faxFolderIdModifiedDate,
      styles: { minWidth: '110px' },
      isSortable: true,
      type: ColumnType.TimeSpent,
      isVisible: !isArchivedPage,
    };

    const previewButton: TableColumnConfig = {
      header: '',
      field: InboundFaxesTableModelKeys.documentStorageLocation,
      styles: { minWidth: '110px' },
      isSortable: false,
      type: ColumnType.Preview,
      buttonConfig: {
        buttonLabel: 'Preview',
        buttonIcon: 'pi-external-link',
        onClicked: (target: InboundFaxesTableModel) => this.previewClicked(target),
        getToolTip: (target: InboundFaxesTableModel) => target.documentStorageLocation,
      },
      isVisible: true,
    };

    const archiveButton: TableColumnConfig = {
      header: '',
      field: InboundFaxesTableModelKeys.isArchived,
      styles: { minWidth: '110px' },
      isSortable: false,
      type: ColumnType.Button,
      buttonConfig: {
        buttonIcon: 'pi-trash',
        emptyLabel: '',
        emptyIcon: 'pi-trash',
        onClicked: (target: InboundFaxesTableModel) => {
          this.archiveFaxClicked(target);
        },
        getToolTip: (target: InboundFaxesTableModel) => target.isArchived,
        disabled: (target: InboundFaxesTableModel) => {
          return target.assignedUserId === null;
        },
      },
      isVisible: !isArchivedPage,
    };

    const restoreArchiveButton: TableColumnConfig = {
      header: '',
      field: InboundFaxesTableModelKeys.restoreArchive,
      styles: { minWidth: '110px' },
      isSortable: false,
      type: ColumnType.RestoreButton,
      buttonConfig: {
        buttonLabel: ' ',
        buttonIcon: 'pi-replay',
        onClicked: (target: InboundFaxesTableModel) => {
          this.restoreArchiveFaxClicked(target);
        },
      },
      isVisible: isArchivedPage,
    };

    // Load filter values from storage only if inbox is selected
    if (selectedInbox) {
      this.inboundFaxesTableQueryService.initQuery(selectedInbox, isArchivedPage);
      const oDataQuery: InboundFaxesQuery = this.inboundFaxesTableQueryService.getQuery();
      faxFolderName.filterConfig.values = oDataQuery[faxFolderName.field];
      assignedUserName.filterConfig.values = oDataQuery[assignedUserName.field];
      memberSubscriberNumber.filterConfig.value = oDataQuery[memberSubscriberNumber.field];
      statusName.filterConfig.values = oDataQuery[statusName.field];
      requestTypeName.filterConfig.values = oDataQuery[requestTypeName.field];
    }

    switch (selectedInbox?.applicationKey) {
      case InboxApplicationKey.AppealsGrievances.toString():
        assignedUserName.styles = { minWidth: '500px' };
        memberSubscriberNumber.styles = { minWidth: '500px' };
        return [
          assignedUserName,
          memberSubscriberNumber,
          faxReceivedDateTime,
          archivedReasonDescription,
          timeInFaxFolder,
          previewButton,
          archiveButton,
          restoreArchiveButton,
        ];
      case InboxApplicationKey.PSCCR.toString():
        assignedUserName.styles = { minWidth: '500px' };
        memberSubscriberNumber.styles = { minWidth: '500px' };
        return [
          assignedUserName,
          memberSubscriberNumber,
          faxReceivedDateTime,
          archivedReasonDescription,
          timeInFaxFolder,
          previewButton,
          archiveButton,
          restoreArchiveButton,
        ];
      default:
        return [
          faxFolderName,
          assignedUserName,
          memberSubscriberNumber,
          faxReceivedDateTime,
          statusName,
          requestTypeName,
          note,
          archivedReasonDescription,
          timeInFaxFolder,
          previewButton,
          archiveButton,
          restoreArchiveButton,
        ];
    }
  }

  public async populateTableDropdowns(
    columnConfig: TableColumnConfig[],
    inbox: InboxDto,
    currentUserId: string
  ): Promise<TableColumnConfig[]> {
    const results = columnConfig;
    const unassignedSelectItem: SelectItem = {
      value: 'unassigned',
      label: 'Unassigned',
    };

    if (results.find((x) => x.field === InboundFaxesTableModelKeys.faxFolderName)?.isVisible) {
      const options = (await this.lookupService.getFaxFolderReferences(inbox)).map(
        (x): SelectItem => ({
          label: x.description,
          value: x.applicationKey,
        })
      );

      results.find((x) => x.field === InboundFaxesTableModelKeys.faxFolderName).filterConfig.options = options;
    }

    if (results.find((x) => x.field === InboundFaxesTableModelKeys.assignedUserName)?.isVisible) {
      const options = await this.lookupService.getAssignedUsersFilter(inbox);

      results.find((x) => x.field === InboundFaxesTableModelKeys.assignedUserName).filterConfig.options = [
        unassignedSelectItem,
      ].concat(options);
    }

    if (results.find((x) => x.field === InboundFaxesTableModelKeys.statusName)?.isVisible) {
      const options = (await this.lookupService.getStatusReferences()).map(
        (x): SelectItem => ({
          label: x.description,
          value: x.applicationKey,
        })
      );
      results.find((x) => x.field === InboundFaxesTableModelKeys.statusName).filterConfig.options = [
        unassignedSelectItem,
      ].concat(options);
    }

    if (results.find((x) => x.field === InboundFaxesTableModelKeys.requestTypeName)?.isVisible) {
      const options = (await this.lookupService.getRequestTypeReferences(inbox)).map(
        (x): SelectItem => ({
          label: x.description,
          value: x.applicationKey,
        })
      );
      results.find((x) => x.field === InboundFaxesTableModelKeys.requestTypeName).filterConfig.options = [
        unassignedSelectItem,
      ].concat(options);
    }

    // Fetching to cache results for later use
    await this.lookupService.getArchivedReasonReferences();
    await this.lookupService.getUsersDropdown(currentUserId);
    await this.lookupService.getDocumentTypes(inbox);

    return results;
  }

  public clearFilters(columnConfig: TableColumnConfig[]): TableColumnConfig[] {
    const updatedColumns = columnConfig;
    updatedColumns
      .filter((x) => !!x.filterConfig?.template)
      .forEach((x) => {
        if (x.filterConfig.template === FilterTemplate.MultiSelectFilterTemplate) {
          x.filterConfig.values = [];
        } else if (x.filterConfig.template === FilterTemplate.SearchFilterTemplate) {
          x.filterConfig.value = '';
        }
      });

    return updatedColumns;
  }

  public updateFilterValues(columnConfig: TableColumnConfig[], event: TableLazyLoadEvent): TableColumnConfig[] {
    let updatedColumns = columnConfig;
    Object.keys(event.filters).forEach((key) => {
      const filter = event.filters[key] as FilterMetadata;
      if (filter?.matchMode === 'contains' && updatedColumns.find((x) => x.field === key)) {
        updatedColumns.find((x) => x.field === key).filterConfig.value = filter.value;
      } else if (filter?.matchMode === 'in' && updatedColumns.find((x) => x.field === key)) {
        updatedColumns.find((x) => x.field === key).filterConfig.values = filter.value;
      }
    });

    return updatedColumns;
  }
}
